import { ReactNode } from 'react';
import BadgeV3 from '../../badge/BadgeV3';
import { Popover, Tab } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { TooltipV2 } from '../../tooltip/TooltipV2';
import { motion, AnimatePresence } from 'framer-motion';

type ColorOption = 'gray' | 'red' | 'yellow' | 'green' | 'blue' | 'violet' | 'indigo' | 'pink' | 'orange';

export type StandardTabProps = {
    // tab label text
    label: string;

    // child elements displayed in popover-type tabs
    children?: ReactNode[] | ReactNode | (({ close }: { close: () => void }) => ReactNode);

    // whether to display tab as pill-type or default
    pill?: boolean;

    // disabled props (state and tooltip label)
    isDisabled?: boolean;
    isDisabledHint?: string;

    // badge props (label and color type)
    badge?: string;
    badgeColor?: ColorOption;
    badgeIcon?: ReactNode;
    badgeRounded?: boolean;

    // color mode; to adjust tab colors depending on container background
    colorMode?: 'dark' | 'light';
};

export default function StandardTab({
    label,
    children,
    pill,
    badge,
    badgeColor,
    badgeIcon,
    badgeRounded = true,
    isDisabled,
    isDisabledHint,
    colorMode,
}: StandardTabProps): JSX.Element {
    const styles = {
        tab: {
            default: {
                unselected: `${
                    colorMode === 'light' ? 'text-gray-500 dark:text-gray-400' : 'text-white dark:text-white'
                } whitespace-nowrap py-2 font-medium text-sm cursor-pointer group inline-flex items-center cursor:pointer outline-none px-1 h-[36px]`,
                selected: `${colorMode === 'light' ? 'text-indigo-600' : 'text-white'} dark:text-indigo-500`,
                disabled: 'opacity-75 cursor-not-allowed',
            },
            pill: {
                unselected: `${
                    colorMode === 'light' ? 'text-gray-500 dark:text-gray-400' : 'text-white dark:text-white'
                } whitespace-nowrap py-2 font-medium text-sm cursor-pointer group inline-flex items-center cursor:pointer outline-none px-4 h-[36px]`,
                selected: `${colorMode === 'light' ? 'text-indigo-700' : 'text-white'} dark:text-indigo-100 rounded-md py-3`,
                disabled: 'opacity-75 cursor-not-allowed',
            },
        },
    };

    return (
        <TooltipV2 disabled={!isDisabled} message={isDisabledHint} position="bottom">
            {children ? (
                <Tab disabled={isDisabled} className="outline-none z-50 relative">
                    {({ selected }): JSX.Element => (
                        <div>
                            <Popover>
                                {({ open, close }): JSX.Element => (
                                    <>
                                        <Popover.Button
                                            className={`${pill ? styles.tab.pill.unselected : styles.tab.default.unselected} ${
                                                open ? (pill ? styles.tab.pill.selected : styles.tab.default.selected) : null
                                            } ${isDisabled && (pill ? styles.tab.pill.disabled : styles.tab.default.disabled)}`}
                                        >
                                            <span className={`${badge && 'mr-2'}`}>{label}</span>
                                            {badge && (
                                                <BadgeV3 color={badgeColor} rounded={badgeRounded} label={badge} leftIcon={badgeIcon} />
                                            )}
                                            <ChevronDownIcon className="w-5 h-5 ml-2" />
                                        </Popover.Button>

                                        <AnimatePresence initial={false}>
                                            {!isDisabled && children && open && (
                                                <Popover.Panel
                                                    static
                                                    as={motion.div}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: 10 }}
                                                    transition={{ duration: 0.1 }}
                                                    className="absolute rounded-md border-[1px] p-3 mt-3 shadow-md bg-white dark:bg-gray-800 dark:border-gray-700"
                                                >
                                                    {typeof children === 'function' ? children({ close }) : children}
                                                </Popover.Panel>
                                            )}
                                        </AnimatePresence>
                                    </>
                                )}
                            </Popover>
                        </div>
                    )}
                </Tab>
            ) : (
                <Tab
                    disabled={isDisabled}
                    className={({ selected }): string =>
                        `${pill ? styles.tab.pill.unselected : styles.tab.default.unselected} ${
                            selected && !isDisabled ? (pill ? styles.tab.pill.selected : styles.tab.default.selected) : ''
                        } ${isDisabled && (pill ? styles.tab.pill.disabled : styles.tab.default.disabled)}`
                    }
                >
                    {({ selected }): JSX.Element => (
                        <>
                            <span className={`${badge && 'mr-2'}`}>{label}</span>
                            {badge && <BadgeV3 color={badgeColor} rounded={badgeRounded} label={badge} leftIcon={badgeIcon} />}
                        </>
                    )}
                </Tab>
            )}
        </TooltipV2>
    );
}
